import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import { Wheat, CheckCircle2, ChevronDown, Search, AlertCircle, ArrowLeft, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cls = 'w-full px-3.5 py-2.5 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm bg-white';

const JENIS = ['Masjid', 'Mushola', 'Surau', 'UPZ', 'Lainnya'];
const STEPS = ['Data Lembaga', 'Wilayah', 'Konfirmasi'];

function RegionSelect({ label, value, onChange, options, loading, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-medium text-stone-600 mb-1.5">{label}</label>
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)}
          className={`${cls} pr-8 ${loading ? 'opacity-60' : ''}`} disabled={loading || options.length === 0}>
          <option value="">{loading ? 'Memuat...' : placeholder}</option>
          {options.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-stone-400 pointer-events-none" />
      </div>
    </div>
  );
}

export default function DaftarUPZ() {
  const [step, setStep] = useState(0);
  const [submitting, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [checkEmail, setCheckEmail] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showCheck, setShowCheck] = useState(false);

  const [form, setForm] = useState({
    nama_upz: '', jenis_lembaga: 'Masjid', nama_pj: '', email: '', phone: '', alamat: '',
    regency_id: '', regency_name: '',
    district_id: '', district_name: '',
    village_id: '', village_name: '',
  });
  const [regencies, setRegencies] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loadingDist, setLoadingDist] = useState(false);
  const [loadingVil, setLoadingVil] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/regions?level=regency&limit=50`).then(r => setRegencies(r.data)).catch(() => {});
  }, []);

  const handleRegency = async (id) => {
    const sel = regencies.find(r => r.id === id);
    setForm(f => ({ ...f, regency_id: id, regency_name: sel?.name || '', district_id: '', district_name: '', village_id: '', village_name: '' }));
    setDistricts([]); setVillages([]);
    if (!id) return;
    setLoadingDist(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/regions?parent_id=${id}&limit=100`);
      setDistricts(data);
    } catch {} finally { setLoadingDist(false); }
  };

  const handleDistrict = async (id) => {
    const sel = districts.find(r => r.id === id);
    setForm(f => ({ ...f, district_id: id, district_name: sel?.name || '', village_id: '', village_name: '' }));
    setVillages([]);
    if (!id) return;
    setLoadingVil(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/regions?parent_id=${id}&limit=200`);
      setVillages(data);
    } catch {} finally { setLoadingVil(false); }
  };

  const handleVillage = (id) => {
    const sel = villages.find(r => r.id === id);
    setForm(f => ({ ...f, village_id: id, village_name: sel?.name || '' }));
  };

  const validateStep0 = () => {
    if (!form.nama_upz.trim()) return toast.error('Nama UPZ/lembaga wajib diisi');
    if (!form.nama_pj.trim()) return toast.error('Nama penanggung jawab wajib diisi');
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error('Email tidak valid');
    if (!form.phone.trim()) return toast.error('Nomor HP wajib diisi');
    return true;
  };

  const validateStep1 = () => {
    if (!form.regency_id) return toast.error('Pilih Kabupaten/Kota');
    return true;
  };

  const nextStep = () => {
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    setStep(s => Math.min(s + 1, 2));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/upz/register`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mendaftar. Coba lagi.');
    } finally { setSaving(false); }
  };

  const handleCheckStatus = async () => {
    if (!checkEmail.trim()) return;
    setChecking(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/upz/check-status?email=${encodeURIComponent(checkEmail)}`);
      setCheckResult(data);
    } catch (err) {
      setCheckResult({ error: err.response?.data?.detail || 'Tidak ditemukan' });
    } finally { setChecking(false); }
  };

  const statusColor = (s) => s === 'disetujui' ? 'text-emerald-600 bg-emerald-50' : s === 'ditolak' ? 'text-red-600 bg-red-50' : 'text-amber-600 bg-amber-50';
  const statusLabel = (s) => s === 'disetujui' ? 'Disetujui' : s === 'ditolak' ? 'Ditolak' : 'Menunggu Verifikasi';

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl border border-stone-200 p-10 max-w-md w-full text-center shadow-lg">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Pendaftaran Terkirim!</h2>
          <p className="text-stone-500 text-sm mb-6">
            Pendaftaran UPZ <strong>{form.nama_upz}</strong> berhasil dikirim. Tim Kemenag akan memverifikasi dalam 1–3 hari kerja.
          </p>
          <div className="bg-amber-50 rounded-xl p-4 text-sm text-amber-700 mb-6 text-left">
            <p className="font-medium mb-1">Informasi Penting:</p>
            <ul className="space-y-1 list-disc list-inside text-xs">
              <li>Pantau status melalui tombol "Cek Status" di bawah</li>
              <li>Setelah disetujui, login info akan dikirimkan via WhatsApp</li>
              <li>Login di: <strong>masjidberdaya.id/login</strong></li>
            </ul>
          </div>
          <button onClick={() => { setSubmitted(false); setStep(0); setShowCheck(true); }}
            className="w-full py-3 bg-emerald-700 text-white rounded-xl font-medium text-sm">
            Cek Status Pendaftaran
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-800 to-emerald-900 text-white py-10 px-4 text-center">
        <div className="inline-flex w-14 h-14 rounded-2xl bg-white/20 items-center justify-center mb-3">
          <Wheat className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-2xl font-bold mb-1">Daftar UPZ Zakat Ramadan</h1>
        <p className="text-emerald-200 text-sm max-w-md mx-auto">
          Bergabunglah dengan jaringan pengumpulan zakat fitrah digital masjidberdaya.id
        </p>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">

        {/* Check Status Section */}
        <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-6">
          <button onClick={() => setShowCheck(!showCheck)}
            className="flex items-center justify-between w-full text-sm font-medium text-stone-700">
            <span className="flex items-center gap-2"><Search className="w-4 h-4 text-emerald-600" /> Cek Status Pendaftaran</span>
            <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${showCheck ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {showCheck && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden">
                <div className="pt-4 space-y-3">
                  <div className="flex gap-2">
                    <input value={checkEmail} onChange={e => setCheckEmail(e.target.value)}
                      placeholder="Masukkan email yang didaftarkan" type="email"
                      className="flex-1 px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                      onKeyDown={e => e.key === 'Enter' && handleCheckStatus()} />
                    <button onClick={handleCheckStatus} disabled={checking}
                      className="px-4 py-2.5 bg-emerald-700 text-white rounded-xl text-sm font-medium disabled:opacity-60">
                      {checking ? '...' : 'Cek'}
                    </button>
                  </div>
                  {checkResult && (
                    <div className={`rounded-xl p-3 text-sm ${checkResult.error ? 'bg-red-50 text-red-600' : 'bg-stone-50'}`}>
                      {checkResult.error ? (
                        <div className="flex items-center gap-2"><AlertCircle className="w-4 h-4" />{checkResult.error}</div>
                      ) : (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 font-medium text-stone-800">{checkResult.nama_upz}</div>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(checkResult.status)}`}>
                            {statusLabel(checkResult.status)}
                          </span>
                          {checkResult.catatan && <p className="text-xs text-stone-500 mt-1">Catatan: {checkResult.catatan}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-6">
          {STEPS.map((s, i) => (
            <React.Fragment key={i}>
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  i < step ? 'bg-emerald-600 text-white' : i === step ? 'bg-emerald-700 text-white' : 'bg-stone-200 text-stone-500'
                }`}>{i < step ? '✓' : i + 1}</div>
                <span className={`text-xs font-medium hidden sm:block ${i === step ? 'text-emerald-700' : 'text-stone-400'}`}>{s}</span>
              </div>
              {i < 2 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-emerald-500' : 'bg-stone-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Form Steps */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
          {step === 0 && (
            <>
              <h2 className="font-semibold text-stone-800">Data Lembaga</h2>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">Jenis Lembaga *</label>
                <div className="flex gap-2 flex-wrap">
                  {JENIS.map(j => (
                    <button type="button" key={j} onClick={() => setForm(f => ({ ...f, jenis_lembaga: j }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        form.jenis_lembaga === j ? 'bg-emerald-700 border-emerald-700 text-white' : 'border-stone-200 text-stone-600 hover:border-emerald-300'
                      }`}>{j}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">Nama {form.jenis_lembaga} *</label>
                <input value={form.nama_upz} onChange={e => setForm(f => ({ ...f, nama_upz: e.target.value }))}
                  className={cls} placeholder={`Nama ${form.jenis_lembaga}`} />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">Nama Penanggung Jawab *</label>
                <input value={form.nama_pj} onChange={e => setForm(f => ({ ...f, nama_pj: e.target.value }))}
                  className={cls} placeholder="Nama Ketua/Pengurus" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">Email *</label>
                  <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className={cls} type="email" placeholder="email@contoh.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">No. HP *</label>
                  <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className={cls} type="tel" placeholder="08xxxxxxxxxx" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">Alamat</label>
                <textarea value={form.alamat} onChange={e => setForm(f => ({ ...f, alamat: e.target.value }))}
                  className={cls} rows={2} placeholder="Alamat lengkap lembaga" />
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h2 className="font-semibold text-stone-800 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-600" /> Wilayah Kepulauan Riau
              </h2>
              <RegionSelect label="Kabupaten/Kota *" value={form.regency_id} onChange={handleRegency}
                options={regencies} placeholder="— Pilih Kabupaten/Kota —" />
              <RegionSelect label="Kecamatan" value={form.district_id}
                onChange={handleDistrict} options={districts} loading={loadingDist}
                placeholder={form.regency_id ? '— Pilih Kecamatan —' : '— Pilih Kab/Kota dulu —'} />
              <RegionSelect label="Desa/Kelurahan" value={form.village_id}
                onChange={handleVillage} options={villages} loading={loadingVil}
                placeholder={form.district_id ? '— Pilih Desa/Kelurahan —' : '— Pilih Kecamatan dulu —'} />
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-semibold text-stone-800">Konfirmasi Data</h2>
              <div className="bg-stone-50 rounded-xl p-4 space-y-2 text-sm">
                {[
                  ['Jenis', form.jenis_lembaga],
                  ['Nama Lembaga', form.nama_upz],
                  ['Penanggung Jawab', form.nama_pj],
                  ['Email', form.email],
                  ['No. HP', form.phone],
                  ['Alamat', form.alamat || '-'],
                  ['Kabupaten/Kota', form.regency_name || '-'],
                  ['Kecamatan', form.district_name || '-'],
                  ['Desa/Kelurahan', form.village_name || '-'],
                ].map(([label, val]) => (
                  <div key={label} className="flex gap-2">
                    <span className="text-stone-400 w-36 flex-shrink-0">{label}</span>
                    <span className="font-medium text-stone-800">{val}</span>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 rounded-xl p-3 text-xs text-amber-700">
                Dengan mendaftar, Anda menyetujui penggunaan data untuk keperluan administrasi zakat fitrah Ramadan di <strong>masjidberdaya.id</strong>.
              </div>
            </>
          )}

          <div className="flex justify-between pt-2">
            {step > 0 ? (
              <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-1 px-4 py-2.5 border border-stone-200 rounded-xl text-sm text-stone-600 hover:bg-stone-50">
                <ArrowLeft className="w-4 h-4" /> Kembali
              </button>
            ) : <div />}
            {step < 2 ? (
              <button onClick={nextStep} data-testid="upz-next-btn"
                className="px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-medium transition-colors">
                Lanjut →
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting} data-testid="submit-upz-btn"
                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-60">
                {submitting ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {submitting ? 'Mengirim...' : 'Kirim Pendaftaran'}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-stone-400 mt-6">
          Sudah punya akun? <a href="/login" className="text-emerald-600 hover:underline font-medium">Masuk di sini</a>
        </p>
      </div>
    </div>
  );
}
